import { useCallback, useEffect, useState } from 'react';
import { Lock, Calculator, FileLock2, Send, CalendarClock } from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import PageShell from '../components/PageShell';
import StatCard from '../components/StatCard';
import StatusPill from '../components/StatusPill';
import {
  controlClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../components/FilterBar';
import { formatCurrency } from '../lib/format';
import {
  fetchTaxEstimate,
  fetchTaxChart,
  stageTaxFiling,
  transmitTaxFiling,
  fetchEntityDeadlines,
  computeEntityDeadlines,
  type TaxEstimateResponse,
  type StageTaxFilingRequest,
  type TaxChartResponse,
  type EntityDeadline,
} from '../lib/api';
import { ChartCard, ChartSkeleton, BarChart } from '../components/charts';

interface StagedFiling {
  filingId: string;
  approvalId: string;
  label: string;
  transmitted: boolean;
}

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];
const QUARTERS: StageTaxFilingRequest['quarter'][] = ['Q1', 'Q2', 'Q3', 'Q4'];
const FORM_TYPES: NonNullable<StageTaxFilingRequest['form_type']>[] = ['1040es', '1120es', '941', 'sales', 'other'];

function Tax() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [quarter, setQuarter] = useState<StageTaxFilingRequest['quarter']>('Q1');
  const [formType, setFormType] = useState<NonNullable<StageTaxFilingRequest['form_type']>>('1040es');
  const [estimate, setEstimate] = useState<TaxEstimateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [staging, setStaging] = useState(false);
  const [staged, setStaged] = useState<StagedFiling[]>([]);
  const [transmitting, setTransmitting] = useState<string | null>(null);
  const [filingChart, setFilingChart] = useState<TaxChartResponse | null>(null);
  const [chartLoading, setChartLoading] = useState(true);
  const [deadlines, setDeadlines] = useState<EntityDeadline[]>([]);
  const [deadlinesLoading, setDeadlinesLoading] = useState(true);

  const loadDeadlines = useCallback(async () => {
    setDeadlinesLoading(true);
    const result = await fetchEntityDeadlines(90);
    if (result.ok && result.data?.deadlines) {
      setDeadlines(result.data.deadlines);
    } else {
      setDeadlines([]);
    }
    setDeadlinesLoading(false);
  }, []);

  const loadEstimate = useCallback(async () => {
    setLoading(true);
    const [estimateResult, chartResult] = await Promise.all([
      fetchTaxEstimate(year, quarter),
      fetchTaxChart(year),
      loadDeadlines(),
    ]);
    if (estimateResult.ok && estimateResult.data) setEstimate(estimateResult.data);
    else setEstimate(null);
    if (chartResult.ok && chartResult.data) setFilingChart(chartResult.data);
    else setFilingChart(null);
    setLoading(false);
    setChartLoading(false);
  }, [year, quarter, loadDeadlines]);

  useEffect(() => {
    loadEstimate();
  }, [loadEstimate]);

  const handleStage = async () => {
    setStaging(true);
    const result = await stageTaxFiling({
      year,
      quarter,
      form_type: formType,
      estimated_tax: estimate?.estimated_tax ?? undefined,
    });
    setStaging(false);
    if (result.ok && result.data) {
      toast.success('Filing staged for approval');
      setStaged((prev) => [
        {
          filingId: result.data!.filing_id,
          approvalId: result.data!.approval_id,
          label: `${formType.toUpperCase()} · ${year} ${quarter}`,
          transmitted: false,
        },
        ...prev,
      ]);
      const chartResult = await fetchTaxChart(year);
      if (chartResult.ok && chartResult.data) setFilingChart(chartResult.data);
    }
  };

  const handleTransmit = async (filing: StagedFiling) => {
    setTransmitting(filing.filingId);
    const result = await transmitTaxFiling(filing.filingId, filing.approvalId);
    setTransmitting(null);
    if (result.ok && result.data) {
      toast.success('Filing transmitted');
      setStaged((prev) =>
        prev.map((f) => (f.filingId === filing.filingId ? { ...f, transmitted: true } : f))
      );
    }
  };

  const handleRecomputeDeadlines = async () => {
    const result = await computeEntityDeadlines(true);
    if (result.ok) {
      toast.success('Entity deadlines refreshed from formation facts');
      await loadDeadlines();
    }
  };

  const notConfigured = estimate?.status === 'not_configured';

  const filingProgressData =
    filingChart?.data.map((row) => ({
      quarter: row.quarter,
      progress:
        row.status === 'transmitted'
          ? 100
          : row.status === 'pending' || row.status === 'staged'
          ? 60
          : 0,
    })) ?? [];

  return (
    <PageShell
      title="Tax"
      subtitle="Estimate quarterly taxes and stage filings for human approval before transmission."
    >
      <ChartCard
        title="Filing status"
        subtitle={
          filingChart
            ? `${filingChart.completed} of ${filingChart.total} quarters in progress · ${year}`
            : `${year} quarterly filings`
        }
      >
        {chartLoading ? (
          <ChartSkeleton height={200} />
        ) : (
          <BarChart
            data={filingProgressData}
            xKey="quarter"
            series={[{ key: 'progress', label: 'Progress %', color: '#3B82F6' }]}
            valueFormatter={(v) => `${v}%`}
            height={200}
          />
        )}
      </ChartCard>

      <div className="rounded-xl border border-zt-border bg-zt-surface p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CalendarClock className="text-zt-warning" size={18} />
            <h3 className="font-semibold text-white">Entity deadlines</h3>
          </div>
          <button type="button" onClick={handleRecomputeDeadlines} className={secondaryButtonClass}>
            Recompute from entity memory
          </button>
        </div>
        {deadlinesLoading ? (
          <p className="text-sm text-zt-muted">Loading deadlines…</p>
        ) : deadlines.length === 0 ? (
          <p className="text-sm text-zt-muted">
            Upload founding documents via{' '}
            <Link to="/files" className="text-zt-primary hover:text-zt-accent">
              Files
            </Link>{' '}
            or{' '}
            <Link to="/receipts" className="text-zt-primary hover:text-zt-accent">
              Receipts
            </Link>{' '}
            to extract formation date and generate compliance deadlines.
          </p>
        ) : (
          <div className="space-y-2">
            {deadlines.slice(0, 8).map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between rounded-lg border border-zt-border bg-zt-bg px-3 py-2 text-sm"
              >
                <span className="text-slate-200">{item.title}</span>
                <span className="text-zt-muted">{item.dueDate?.slice(0, 10) ?? '—'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-zt-border bg-zt-surface p-6">
        <div className="mb-4 flex items-center gap-2">
          <Calculator className="text-zt-primary" size={18} />
          <h3 className="font-semibold text-white">Quarterly estimate</h3>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-200">Year</label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={controlClass}>
              {YEARS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-200">Quarter</label>
            <select
              value={quarter}
              onChange={(e) => setQuarter(e.target.value as StageTaxFilingRequest['quarter'])}
              className={controlClass}
            >
              {QUARTERS.map((q) => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </select>
          </div>
          <button type="button" onClick={loadEstimate} className={secondaryButtonClass}>
            <Calculator size={16} /> Estimate
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Revenue" value={loading ? '…' : estimate?.revenue ? formatCurrency(estimate.revenue) : '—'} tone="info" />
        <StatCard label="Expenses" value={loading ? '…' : estimate?.expenses ? formatCurrency(estimate.expenses) : '—'} tone="neutral" />
        <StatCard label="Taxable income" value={loading ? '…' : estimate?.taxable_income ? formatCurrency(estimate.taxable_income) : '—'} tone="warning" />
        <StatCard
          label="Estimated tax"
          value={loading ? '…' : estimate?.estimated_tax ? formatCurrency(estimate.estimated_tax) : '—'}
          tone="danger"
          icon={<Lock size={18} />}
        />
      </div>

      {notConfigured && (
        <div className="rounded-lg border border-zt-warning/30 bg-zt-warning/10 px-4 py-3 text-sm text-zt-warning">
          ERPNext is not configured, so revenue and expenses could not be computed. You can still
          stage a filing for approval.
        </div>
      )}

      <div className="rounded-xl border border-zt-border bg-zt-surface p-6">
        <div className="mb-4 flex items-center gap-2">
          <FileLock2 className="text-zt-info" size={18} />
          <h3 className="font-semibold text-white">Stage a filing</h3>
        </div>
        <p className="text-sm text-zt-muted">
          Staging creates a high-risk approval request. Transmission is only permitted after the
          approval is granted in the{' '}
          <Link to="/ai" className="text-zt-primary hover:text-zt-accent">
            approval queue
          </Link>
          .
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-200">Form type</label>
            <select
              value={formType}
              onChange={(e) => setFormType(e.target.value as NonNullable<StageTaxFilingRequest['form_type']>)}
              className={controlClass}
            >
              {FORM_TYPES.map((f) => (
                <option key={f} value={f}>
                  {f.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          <button type="button" onClick={handleStage} disabled={staging} className={primaryButtonClass}>
            <FileLock2 size={16} /> {staging ? 'Staging…' : 'Stage for approval'}
          </button>
        </div>
      </div>

      {staged.length > 0 && (
        <div className="rounded-xl border border-zt-border bg-zt-surface p-6">
          <h3 className="mb-4 font-semibold text-white">Staged filings (this session)</h3>
          <div className="space-y-3">
            {staged.map((filing) => (
              <div
                key={filing.filingId}
                className="flex flex-col gap-3 rounded-lg border border-zt-border bg-zt-bg p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <div className="text-sm font-medium text-slate-200">{filing.label}</div>
                  <div className="text-xs text-zt-muted">
                    Filing {filing.filingId} · Approval {filing.approvalId}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusPill status={filing.transmitted ? 'transmitted' : 'pending'} />
                  {!filing.transmitted && (
                    <button
                      type="button"
                      onClick={() => handleTransmit(filing)}
                      disabled={transmitting === filing.filingId}
                      className={primaryButtonClass}
                    >
                      <Send size={14} /> {transmitting === filing.filingId ? 'Transmitting…' : 'Transmit'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </PageShell>
  );
}

export default Tax;
